import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { BpmClient, TaskFormVariables, Task } from "@amaster.ai/bpm-client";

export interface ApproveTaskPageProps {
  /** Task ID to approve */
  taskId: string;
  /** BPM client instance */
  bpmClient: BpmClient;
  /** Render form fields based on form variables */
  renderForm: (
    formVariables: TaskFormVariables,
    onFieldChange: (field: string, value: unknown) => void
  ) => React.ReactNode;
  /** Called after successful approval */
  onApproved?: () => void;
  /** Called after rejection */
  onRejected?: () => void;
  /** Called on cancel */
  onCancel?: () => void;
  /** Additional variables to include when completing task */
  additionalVariables?: Record<string, unknown>;
  /** Additional class name */
  className?: string;
}

/**
 * Approve Task Page - displays task details and form for approval
 */
export function ApproveTaskPage({
  taskId,
  bpmClient,
  renderForm,
  onApproved,
  onRejected,
  onCancel,
  additionalVariables = {},
  className = "",
}: ApproveTaskPageProps) {
  const { t } = useTranslation("bpm");
  const [task, setTask] = useState<Task | null>(null);
  const [formVariables, setFormVariables] = useState<TaskFormVariables>({});
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTaskData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [taskRes, varsRes] = await Promise.all([
        bpmClient.getTask(taskId),
        bpmClient.getTaskFormVariables(taskId),
      ]);

      if (taskRes.error) {
        setError(taskRes.error.message);
        return;
      }
      if (varsRes.error) {
        setError(varsRes.error.message);
        return;
      }

      setTask(taskRes.data);
      setFormVariables(varsRes.data || {});
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load task");
    } finally {
      setLoading(false);
    }
  }, [bpmClient, taskId]);

  useEffect(() => {
    if (taskId) {
      fetchTaskData();
    }
  }, [taskId, fetchTaskData]);

  const handleFieldChange = (field: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleComplete = async (approved: boolean) => {
    setSubmitting(true);
    setError(null);
    try {
      const variables = {
        ...formValues,
        ...additionalVariables,
        approvalStatus: approved ? "approved" : "rejected",
      };

      const { error: completeError } = await bpmClient.completeTask(taskId, variables);

      if (completeError) {
        setError(completeError.message);
        return;
      }

      if (approved) {
        onApproved?.();
      } else {
        onRejected?.();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to complete task");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={`bpm-approve-page bpm-loading ${className}`}>
        {t("approveTask.loading", "Loading...")}
      </div>
    );
  }

  if (!task) {
    return (
      <div className={`bpm-approve-page ${className}`}>
        <div className="bpm-error">{t("approveTask.notFound", "Task not found")}</div>
      </div>
    );
  }

  return (
    <div className={`bpm-approve-page ${className}`}>
      <div className="bpm-approve-card">
        <div className="bpm-approve-header">
          <h2 className="bpm-approve-title">{task.name}</h2>
        </div>
        <div className="bpm-approve-content">
          {error && <div className="bpm-error">{error}</div>}

          <div className="bpm-approve-form">{renderForm(formVariables, handleFieldChange)}</div>

          <div className="bpm-approve-actions">
            <button
              type="button"
              onClick={() => handleComplete(true)}
              disabled={submitting}
              className="bpm-btn bpm-btn-primary"
            >
              {submitting
                ? t("approveTask.submitting", "Submitting...")
                : t("approveTask.approve", "Approve")}
            </button>
            <button
              type="button"
              onClick={() => handleComplete(false)}
              disabled={submitting}
              className="bpm-btn bpm-btn-danger"
            >
              {submitting
                ? t("approveTask.submitting", "Submitting...")
                : t("approveTask.reject", "Reject")}
            </button>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                className="bpm-btn bpm-btn-outline"
              >
                {t("approveTask.cancel", "Cancel")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export type { TaskFormVariables };
